import { BaseScreen, i18n } from '@goodboydigital/astro';
import { Container, Graphics, Point, Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import { ConfirmationWidget } from '../ui/ConfirmationWidget';
import { ScrollRect } from '../ui/ScrollRect';
import { getAvailableLanguages, getLanguageNativeName, LanguageID } from '../ui/TextConfig';
import { LanguageButton, LanguageButtonOpts } from './../ui/buttons/LanguageButton';
import { Panel } from './../ui/Panel';
import { RadioSelector, RadioSelectorStyles } from './../ui/RadioSelector';
import { ScrollBar } from './../ui/ScrollBar';

export class LanguageSelectScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public titleIcon!: Sprite;
    public backButton!: NineSliceButton;
    public settingsPanel!: Panel;

    private _buttonHeight: number;
    private _firstTime: boolean;
    private _selectionContainer!: Container;
    private _confirmationWidget!: ConfirmationWidget;
    private _scrollBar?: ScrollBar;
    private _scrollRect?: ScrollRect;

    protected languageSelector!: RadioSelector;

    private _chosenLanguage!: LanguageID;

    constructor(app: App)
    {
        super(app);
        this.app = app;
        this._buttonHeight = 85;
        this._firstTime = false;
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0.1;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.settingsPanel = new Panel({
            w: 720,
            h: 540,
        });
        this.view.addChild(this.settingsPanel.view);

        this.titleIcon = Sprite.from(Shared.assets.getTexture('icon-language'));
        this.titleIcon.anchor.set(0.5);
        this.titleIcon.scale.set(2);
        this.settingsPanel.view.addChild(this.titleIcon);
        this.titleIcon.y -= 205;

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() => this.toPrevScreen());

        const selectionContainer = new Container();

        selectionContainer.y = -5;
        const mask = new Graphics();
        const maskWidth = 620;
        const maskHeight = 365;

        mask.beginFill(0xFF0000, 0.7);
        mask.drawRect(
            0, 0,
            maskWidth,
            maskHeight,
        );
        mask.position.set(-460, -150);
        selectionContainer.addChild(mask);
        selectionContainer.mask = mask;

        this._selectionContainer = selectionContainer;
        
        const padding = 6;

        this.languageSelector = new RadioSelector({
            buttonType: LanguageButton as any,
            style: RadioSelectorStyles.inlineWrap,
            padding,
            inlineWrapLimit: 800,
            buttons: this._getLanguageMeta(),
        });

        this.languageSelector.signals.valueChanged.connect((btn) =>
        {
            const selection = btn as any as LanguageButton;

            this._promptLanguage(selection.value as unknown as LanguageID);
        });

        selectionContainer.x = 150;

        const topPos = (this.languageSelector.height / 2) - (maskHeight / 2) + this._buttonHeight - padding;

        this.languageSelector.view.y = topPos;

        selectionContainer.addChild(this.languageSelector.view);

        const totalHeight = this.languageSelector.height + this._buttonHeight;

        if (totalHeight > maskHeight)
        {
            const scrollRect = new ScrollRect({
                height: this.settingsPanel.bg.height,
                width: this.settingsPanel.bg.width,
                buttons: this.languageSelector._buttons,
                scrollUpdate: (val) =>
                {
                    if(this._scrollBar){
                        this._scrollBar.setThumbY(this._scrollBar.val - val);
                    }
                },
            });

            scrollRect.view.x = 0;
            scrollRect.view.y = 0;

            this.settingsPanel.view.addChild(scrollRect.view);
            this._scrollRect = scrollRect;
            
            const thumbHeight = (0.15 * maskHeight) / ((totalHeight - maskHeight) / (totalHeight));

            const scrollBar = new ScrollBar({
                barHeight: maskHeight,
                barWidth: 30,
                thumbHeight,
                thumbWidth: 30,
                scrollUpdate: (val) =>
                {
                    this.languageSelector.view.y = topPos - ((padding + totalHeight + padding - maskHeight) * val);
                },
            });

            scrollBar.view.x = (maskWidth / 2) + 15;
            scrollBar.view.y = 25;

            this.settingsPanel.view.addChild(scrollBar.view);
            this._scrollBar = scrollBar;
            
        }
        
        this.settingsPanel.view.addChild(selectionContainer);

        this._confirmationWidget = new ConfirmationWidget();
        this.view.addChild(this._confirmationWidget.view);
        this._confirmationWidget.yesButton.onPress.connect(() => this._languageConfirm());
        this._confirmationWidget.noButton.onPress.connect(() => this._languageNotConfirm());
    }

    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._firstTime = screenData.firstTime ?? false;
        
        if(this._firstTime){
            this.app.screens.main.screens.game.instance.view.y = 1500;
            Shared.game.scenario.viewBg.y = -500;
            Shared.game.sceneView.entitiesContainer.scale.set(0.75);
        }
    }
    
    public update(delta = 1): void
    {
        this.app.clouds?.update(delta);
    }

    show(): void
    {
        this.resize(this.width, this.height);

        this.view.visible = true;

        this.backButton.view.visible = !this._firstTime;
        this._selectionContainer.visible = true;
        if (this._scrollBar) this._scrollBar.view.visible = true;
        if (this._scrollRect) this._scrollRect.view.visible = true;

        if (this._firstTime)
        {
            // Shared.settings.language = '' as any;
            this.languageSelector.enableAll();
        }
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.settingsPanel.view.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this._confirmationWidget.view.position.copyFrom(this.app.stageCenter as Point);
        
        this.app.clouds?.resize(w, h);
    }

    private _getLanguageMeta(): Partial<LanguageButtonOpts>[]
    {
        const meta = [] as Partial<LanguageButtonOpts>[];

        const currentLanguage = Shared.settings.language;
        const availableLanguages = getAvailableLanguages();

        availableLanguages.forEach((key) =>
        {
            meta.push({
                text: getLanguageNativeName(key),
                w: 300,
                h: this._buttonHeight,
                active: key === currentLanguage,
                value: key,
            });
        });

        return meta;
    }

    private _promptLanguage(lang: LanguageID)
    {
        if (!this._firstTime && lang === Shared.settings.language) return;
        this._chosenLanguage = lang;

        this._selectionContainer.visible = false;
        if (this._scrollBar) this._scrollBar.view.visible = false;
        if (this._scrollRect) this._scrollRect.view.visible = false;
        this._confirmationWidget.title.text = getLanguageNativeName(lang);
        this._confirmationWidget.open();
    }

    private _languageConfirm(): void
    {
        this._confirmationWidget.close();
        i18n.changeLanguage(this._chosenLanguage);
        Shared.settings.language = this._chosenLanguage;
        console.log(`[LanguagePlugin] Language changed to: ${i18n.language}`);
        this.toNextScreen();
    }

    private _languageNotConfirm(): void
    {
        this._selectionContainer.visible = true;
        if (this._scrollBar) this._scrollBar.view.visible = true;
        if (this._scrollRect) this._scrollRect.view.visible = true;
        this._confirmationWidget.close();
        this.languageSelector.enableAll();

        if (!this._firstTime)
        {
            const availableLanguages = getAvailableLanguages();
            const index = availableLanguages.indexOf(Shared.settings.language);

            this.languageSelector.setActive(index, true);
        }
    }

    private toPrevScreen(): void
    {
        if (this._confirmationWidget['_opened']) this._languageNotConfirm();
        this.app.controller.toSettings();
    }

    private toNextScreen(): void
    {
        if (!this._firstTime)
        {
            this.app.controller.toSettings();
        }
        else
        {
            this.app.controller.toTerms(this._firstTime);
        }
    }
}
